import { computed } from 'vue'
import { useAuth } from '@/auth'
import { RouteLocationRaw, useRouter } from 'vue-router'
import i18n from '@/plugins/i18n'

export enum Roles {
  Admin = 'admin',
  Member = 'member',
}

// We only add the pages that we are going to use
export enum PagesList {
  Dashboard = 'dashboard',
  ExtractionList = 'extractions',
  ExtractionDetail = 'extractions-id',
  ApprovalList = 'approvals',
  ApprovalDetail = 'approvals-id',
  PassportsList = 'passports',
  RequestsList = 'requests',
  RequestsDetail = 'requests-id',
  RequestsNew = 'requests-new',
  DataAssetList = 'data-assets',
  DataAssetDetail = 'data-assets-dataAssetId',
  DataObjectDetail = 'data-assets-dataAssetId-dataObjectId',
  MaterialsComparison = 'data-assets-dataAssetId-dataObjectId-comparison',
  Library = 'library',
  Assessment = 'assessment',
  DataLookup = 'data-lookup',
  QueryBuilder = 'query-builder',
  AIChat = 'ai-chat',
  SyncConfiguration = 'sync-configuration',
  MappingsList = 'mappings',
  DataSpaceToDataObject = 'dataspace-to-dataobject',
  AgentsList = 'workflows',
  Backlog = 'backlog',
}

export enum Permissions {
  // Dashboad
  OpenDashboard = 'ROLE_OPEN_DASHBOARD',
  UseDashboardExtractions = 'ROLE_USE_DASHBOARD_EXTRACTIONS',
  ApproveDashboardInformation = 'ROLE_APPROVE_DASHBOARD_INFORMATION',
  // Extraction
  OpenExtractions = 'ROLE_OPEN_EXTRACTIONS',
  CreateExtraction = 'ROLE_CREATE_EXTRACTION',
  UpdateExtraction = 'ROLE_UPDATE_EXTRACTION',
  DeleteExtraction = 'ROLE_DELETE_EXTRACTION',
  UploadDocumentsInExtraction = 'ROLE_UPLOAD_DOCUMENTS_IN_EXTRACTION',
  AllowQuickExtraction = 'ROLE_ALLOW_QUICK_EXTRACTION',
  AllowDefineDataAssetExtraction = 'ROLE_ALLOW_DEFINE_DATA_ASSET_EXTRACTION',
  SelectPassportExtraction = 'ROLE_SELECT_PASSPORT_EXTRACTION',
  CreatePassportExtraction = 'ROLE_CREATE_PASSPORT_EXTRACTION',
  // Approval
  OpenApprovals = 'ROLE_OPEN_APPROVALS',
  ApproveExtractedDocuments = 'ROLE_APPROVE_EXTRACTED_DOCUMENTS',
  // Requests
  CreateRequests = 'ROLE_CREATE_REQUESTS',
  UpdateRequests = 'ROLE_UPDATE_REQUESTS',
  ViewRequests = 'ROLE_VIEW_REQUESTS',
  AddProductsToRequests = 'ROLE_ADD_PRODUCTS_TO_REQUESTS',
  AddNewItemsToRequests = 'ROLE_ADD_NEW_ITEMS_TO_REQUESTS',
  RequestsIngredients = 'ROLE_REQUESTS_INGREDIENTS',
  CloseRequestsCommunications = 'ROLE_CLOSE_REQUESTS_COMMUNICATIONS',
  // Data assets
  OpenDataAssets = 'ROLE_OPEN_DATA_ASSETS',
  CreateDataAssets = 'ROLE_CREATE_DATA_ASSETS',
  UpdateDataAssets = 'ROLE_UPDATE_DATA_ASSETS',
  DeleteDataAssets = 'ROLE_DELETE_DATA_ASSETS', // The 'mark data assets' permissions will be handled by the 'delete' permission for now, because is the unique action generated after mark some data asset
  DownloadDataAssets = 'ROLE_DOWNLOAD_DATA_ASSETS',
  ShareDataAssets = 'ROLE_SHARE_DATA_ASSETS',
  // Data objects (Materials)
  CreateMaterials = 'ROLE_CREATE_DATA_OBJECTS',
  SearchMaterials = 'ROLE_SEARCH_MATERIALS',
  OpenMaterials = 'ROLE_OPEN_MATERIALS',
  ShareMaterials = 'ROLE_SHARE_MATERIALS',
  DownloadMaterials = 'ROLE_DOWNLOAD_MATERIALS',
  DeleteMaterials = 'ROLE_DELETE_MATERIALS', // The 'mark materials' permissions will be handled by the 'delete' permission for now, because is the unique action generated after mark some material
  AddReferencesToMaterials = 'ROLE_ADD_REFERENCES_TO_MATERIALS',
  AddInformationToMaterials = 'ROLE_ADD_INFORMATION_TO_MATERIALS',
  AddInformationItemToMaterials = 'ROLE_ADD_INFORMATION_ITEM_TO_MATERIALS',
  AddInformationTemplateToMaterials = 'ROLE_ADD_INFORMATION_TEMPLATE_TO_MATERIALS',
  StoreInSAP = 'ROLE_STORE_IN_SAP',
  SelectPassportsInMaterials = 'ROLE_SELECT_PASSPORTS_IN_MATERIALS',
  CompareMaterials = 'ROLE_COMPARE_MATERIALS',
  SeeDocumentInCompareMaterials = 'ROLE_SEE_DOCUMENTS_IN_COMPARE_MATERIALS',
  UpdateItemsInCompareMaterials = 'ROLE_UPDATE_ITEMS_IN_COMPARE_MATERIALS',
  DeleteItemsInCompareMaterials = 'ROLE_DELETE_ITEMS_IN_COMPARE_MATERIALS',
  SeeVersionHistoryOfMaterials = 'ROLE_SEE_VERSION_HISTORY_OF_MATERIALS',
  // Passports
  OpenPassports = 'ROLE_OPEN_PASSPORTS',
  CreatePassports = 'ROLE_CREATE_PASSPORTS',
  UpdatePassports = 'ROLE_UPDATE_PASSPORTS',
  DeletePassports = 'ROLE_DELETE_PASSPORTS',
  // Library
  OpenLibrary = 'ROLE_OPEN_LIBRARY',
  UploadDocumentsInLibrary = 'ROLE_UPLOAD_DOCUMENTS_IN_LIBRARY',
  OpenDocumentsInLibrary = 'ROLE_OPEN_DOCUMENTS_IN_LIBRARY',
  DeleteDocumentsInLibrary = 'ROLE_DELETE_DOCUMENTS_IN_LIBRARY',
  // Assessments
  OpenAssessment = 'ROLE_OPEN_ASSESSMENT',
  CrudAssessment = 'ROLE_CRUD_ASSESSMENT',
  // Data Lookup
  OpenDataLookup = 'ROLE_OPEN_DATA_LOOKUP',
  CrudDataLookup = 'ROLE_CRUD_DATA_LOOKUP',
  // Query builder
  OpenQueryBuilder = 'ROLE_OPEN_QUERIES',
  // AI CHAT
  OpenAIChat = 'ROLE_OPEN_AI_CHAT',
  // Sync configuration
  OpenSyncConfiguration = 'ROLE_OPEN_SYNC_CONFIGURATION',
  // Mappings
  OpenMappings = 'ROLE_OPEN_MAPPINGS',
  // Data Space to Data Object
  OpenDataSpaceToDataObject = 'ROLE_OPEN_DATA_SPACE_TO_DATA_OBJECT',
  // Agents
  OpenAgents = 'ROLE_OPEN_AGENTS',
  // Backlog
  OpenBacklog = 'ROLE_OPEN_BACKLOG',
  // Other features
  SAPMatching = 'ROLE_SAP_MATCHING',
  // Generate labels
  OpenGenerateLabels = 'ROLE_OPEN_GENERATE_LABELS',
}

interface PageAccessRule {
  anyOf?: Permissions[]
  allOf?: Permissions[]
}

const PAGE_ACCESS_RULES: Record<PagesList, PageAccessRule> = {
  [PagesList.Dashboard]: { anyOf: [Permissions.OpenDashboard] },
  [PagesList.ExtractionList]: { anyOf: [Permissions.OpenExtractions] },
  [PagesList.ExtractionDetail]: {
    anyOf: [Permissions.OpenExtractions],
    allOf: [Permissions.CreateExtraction, Permissions.UpdateExtraction],
  },
  [PagesList.ApprovalList]: { anyOf: [Permissions.OpenApprovals] },
  [PagesList.ApprovalDetail]: { anyOf: [Permissions.OpenApprovals] },
  [PagesList.PassportsList]: {
    anyOf: [Permissions.OpenPassports],
    allOf: [Permissions.CreatePassports, Permissions.UpdatePassports, Permissions.DeletePassports],
  },
  [PagesList.RequestsList]: { anyOf: [Permissions.ViewRequests] },
  [PagesList.RequestsDetail]: { anyOf: [Permissions.ViewRequests] },
  [PagesList.RequestsNew]: { anyOf: [Permissions.CreateRequests] },
  [PagesList.DataAssetList]: { anyOf: [Permissions.OpenDataAssets] },
  [PagesList.DataAssetDetail]: { anyOf: [Permissions.OpenDataAssets] },
  [PagesList.DataObjectDetail]: { anyOf: [Permissions.OpenDataAssets, Permissions.OpenMaterials] },
  [PagesList.MaterialsComparison]: { anyOf: [Permissions.CompareMaterials] },
  [PagesList.Library]: { anyOf: [Permissions.OpenLibrary] },
  [PagesList.Assessment]: { anyOf: [Permissions.OpenAssessment, Permissions.CrudAssessment] },
  [PagesList.DataLookup]: { anyOf: [Permissions.OpenDataLookup, Permissions.CrudDataLookup] },
  [PagesList.QueryBuilder]: { anyOf: [Permissions.OpenQueryBuilder] },
  [PagesList.AIChat]: { anyOf: [Permissions.OpenAIChat] },
  [PagesList.SyncConfiguration]: { anyOf: [Permissions.OpenSyncConfiguration] },
  [PagesList.MappingsList]: { anyOf: [Permissions.OpenMappings] },
  [PagesList.DataSpaceToDataObject]: { anyOf: [Permissions.OpenDataSpaceToDataObject] },
  [PagesList.AgentsList]: { anyOf: [Permissions.OpenAgents] },
  [PagesList.Backlog]: { anyOf: [Permissions.OpenBacklog] },
}

export default function useRolesAndPermissions() {
  const auth = useAuth()
  const router = useRouter()
  const permissionDeniedRoute = {
    name: 'error',
    query: {
      statusCode: '403',
      // eslint-disable-next-line
      // @ts-ignore
      message: i18n.global.t('You do not have permissions to view this page or you are not authenticated'),
    },
  }
  const ROLES_WITH_PERMISSIONS = [
    {
      type: Roles.Admin,
      permissions: Object.values(Permissions), // Assing all the permissions
    },
    {
      type: Roles.Member,
      permissions: [
        Permissions.OpenDashboard,
        Permissions.OpenExtractions,
        Permissions.OpenApprovals,
        Permissions.OpenPassports,
        Permissions.OpenLibrary,
        Permissions.OpenAssessment,
        Permissions.OpenDataLookup,
        Permissions.OpenSyncConfiguration,
        Permissions.OpenMappings,
        Permissions.OpenDataSpaceToDataObject,
        Permissions.OpenAgents,
        Permissions.OpenBacklog,
        Permissions.UploadDocumentsInExtraction,
        Permissions.ApproveDashboardInformation,
        Permissions.UseDashboardExtractions,
        Permissions.SearchMaterials,
        Permissions.ApproveExtractedDocuments,
        Permissions.CreateMaterials,
        Permissions.OpenDataAssets,
        Permissions.UpdateDataAssets,
        Permissions.DownloadDataAssets,
        Permissions.OpenMaterials,
        Permissions.ShareMaterials,
        Permissions.DownloadMaterials,
        Permissions.DeleteMaterials,
        Permissions.AddReferencesToMaterials,
        Permissions.AddInformationToMaterials,
        Permissions.AddInformationItemToMaterials,
        Permissions.AddInformationTemplateToMaterials,
        Permissions.StoreInSAP,
        Permissions.CompareMaterials,
        Permissions.SeeDocumentInCompareMaterials,
        Permissions.UpdateItemsInCompareMaterials,
        Permissions.DeleteItemsInCompareMaterials,
        Permissions.SeeVersionHistoryOfMaterials,
        Permissions.OpenDocumentsInLibrary,
      ],
    },
  ]

  const userRolesWithPermissions = computed(() => {
    if (auth?.user) {
      return {
        permissions: auth.user.permissions || [],
        restrictedPages: getRestrictedPagesByPermissions(auth.user.permissions).map((p) => p.toString()),
      }
    } else {
      return {
        permissions: [],
        restrictedPages: Object.values(PagesList),
      }
    }
  })

  function getRestrictedPagesByPermissions(userPermissions?: readonly string[]) {
    const permissions = userPermissions || []

    return Object.values(PagesList).filter((page) => {
      const rule = PAGE_ACCESS_RULES[page]
      const hasAnyRequiredPermission = rule.anyOf?.some((permission) => permissions.includes(permission)) || false
      const hasAllRequiredPermissions = rule.allOf?.every((permission) => permissions.includes(permission)) || false

      return !hasAnyRequiredPermission && !hasAllRequiredPermissions
    })
  }

  function hasPermission(permission: Permissions) {
    return (
      userRolesWithPermissions.value?.permissions && userRolesWithPermissions.value?.permissions.includes(permission)
    )
  }

  function hasRedirectPermissions(permission: Permissions, path: RouteLocationRaw) {
    router.isReady().then(() => {
      if (userRolesWithPermissions.value.permissions.includes(permission)) {
        router.push(path)
      }
    })
  }

  function toErrorPageDueToInsufficientPermissions() {
    router.isReady().then(() => {
      router.replace(permissionDeniedRoute)
    })
  }

  return {
    ROLES_WITH_PERMISSIONS,
    userRolesWithPermissions,
    Permissions,
    permissionDeniedRoute,
    hasPermission,
    hasRedirectPermissions,
    toErrorPageDueToInsufficientPermissions,
  }
}
